import { useQuery, type QueryClient } from "@tanstack/react-query"
import { directorApi } from "@/services/director-api"
import { queryKeys } from "./query-keys"
import { useAuthReady } from "./use-auth-ready"
import type { DirectorTenant } from "@/types/director"

export function useCurrentTenant() {
  const isAuthReady = useAuthReady()

  return useQuery({
    queryKey: queryKeys.tenant.all,
    queryFn: () => directorApi.getCurrentTenant(),
    enabled: isAuthReady,
    staleTime: 0,
    refetchOnWindowFocus: "always", // tenant can change mid-session (FAB-16186); always re-check on focus
    // Bounded-latency fallback in case a focus/visibility event is missed.
    // Paused while the tab is backgrounded (refetchIntervalInBackground defaults to false).
    refetchInterval: 30_000,
  })
}

/**
 * Reads the current tenant id directly from the cache (no subscription).
 * For imperative call sites (mutation callbacks, SignalR handlers) that need
 * to build a tenant-scoped query key but can't call the useCurrentTenant hook.
 */
export function getCachedTenantId(queryClient: QueryClient): number | undefined {
  return queryClient.getQueryData<DirectorTenant>(queryKeys.tenant.all)?.id
}
